import _cloneDeep from "lodash/cloneDeep";
import React from "react";
import styled from "styled-components";
import { KEY_ARROW_DOWN, KEY_ARROW_UP, KEY_BACKSPACE, KEY_DELETE, KEY_ESCAPE } from "./constants/keyboardCodes";
import { DropdownHandler } from "./DropdownHandler";
import { useDebounce } from "./hooks/useDebounce";
import { useKeyboardNav } from "./hooks/useKeyboardNav";
import { usePortalDropdownCoords } from "./hooks/usePortalDropdownCoords";
import { Icon } from "./Icon";
import {
  CustomIcon,
  Description,
  Dropdown,
  DropdownContainer,
  DropdownElement,
  ErrorIcon,
  ErrorMessage,
  GroupedItem,
  Item,
  Option,
  OptionSubtext,
  OptionText,
  SelectMenuContainer,
  SelectMenuProps,
  Trigger,
} from "./SelectMenu";
import { LoadingState } from "./states/LoadingState";
import { style } from "./style";
import { RequiredAsterisk } from "./TextInputUtils";

const DEBOUNCE_DELAY = 250;

const ArrowIconWrapper = styled.div`
  font-size: ${style.vars.fontSize.small};
  z-index: ${style.vars.zIndex.base};
  flex: 0 0 auto;
  align-self: flex-start;
  padding-top: ${style.vars.space["1"]};
  margin-top: ${style.vars.space["2"]};
`;

const CloseIconWrapper = styled.div`
  font-size: ${style.vars.fontSize.small};
  z-index: ${style.vars.zIndex.base};
  flex: 0 0 auto;
  color: ${style.colors.blue.dark};
`;

const ComboBoxInput = styled.input`
  && {
    padding: 0;
    border: 0;
    background-color: transparent;
    &:focus {
      box-shadow: none;
    }
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const LoaderText = styled.span`
  margin-left: ${style.vars.space["2"]};
`;

const SelectedItems = styled.div`
  display: flex;
  overflow: hidden;
  flex-wrap: wrap;
`;

const SelectedItem = styled.div`
  display: flex;
  background: ${style.colors.grey.light};
  border-radius: ${style.vars.borderRadius.base};
  padding: ${style.vars.whitespace.xsmall} ${style.vars.whitespace.xsmall};
  align-items: center;
  color: ${style.colors.grey.dark};
  overflow: hidden;
  text-overflow: ellipsis;
  margin: ${style.vars.space["1"]};
`;

const GroupTitle = styled.div`
  padding: ${style.vars.whitespace.small} ${style.vars.whitespace.regular};
  color: ${style.colors.grey.dark};
  font-size: ${style.vars.fontSize.small};

  &:not(:first-child) {
    margin-top: ${style.vars.whitespace.regular};
  }
`;

type MultipleSelectionProps = {
  selection?: Item[];
  setSelection: (selection: Item[]) => void;
};

export type MultiSelectComboBoxProps = MultipleSelectionProps &
  SelectMenuProps & {
    isSearching?: boolean;
    setSearching?: (isSearching: boolean) => void;
    onSearch?: (searchTerm: string) => void;
    debounce?: number;
    renderInPortal?: boolean;
    sensitive?: boolean;
    required?: boolean;
  };

export const MultiSelectComboBox: React.FC<MultiSelectComboBoxProps> = ({
  id,
  className,
  items,
  selection = [],
  description,
  disabled,
  error,
  errorMessage,
  placeholder,
  uneditable,
  setSelection,
  isSearching = false,
  setSearching,
  onSearch,
  debounce = DEBOUNCE_DELAY,
  renderInPortal = false,
  sensitive = false,
  required,
}) => {
  const { isMenuOpen, menuRef, inputRef, onKeyUp, onComboBoxKeyDown, onItemKeyUp, onClick, open, close, rover } = useKeyboardNav({
    isMenuOpen: false,
  });
  const { coords, updateCoords, shouldUseCoords } = usePortalDropdownCoords({
    menuRef,
    isMenuOpen,
    renderInPortal,
  });

  const [availableItems, setAvailableItems] = React.useState(items);
  const [currentSearchTerm, setCurrentSearchTerm] = React.useState("");
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [topOfMenuReached, setTopOfMenuReached] = React.useState(false);
  const [isShowingSpinner, setShowingSpinner] = React.useState(true);
  const debouncedSearchTerm = useDebounce(currentSearchTerm, debounce);
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const invalid = error && !isMenuOpen;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const selectable = !disabled && !uneditable && isMenuOpen;
  const searchInputId = id ? `${id}-search` : undefined;

  const isSelected = (value: string) => {
    return selection && selection.some((item) => item.value === value);
  };

  const unSelectedItems = (): Item[] | GroupedItem[] => {
    let isGroupedItems = false;
    const preFilteredItems = (items as GroupedItem[]).filter((item: GroupedItem) => {
      if (item.items) {
        isGroupedItems = true;
        return item.items.some(({ value }) => !isSelected(value));
      }

      const { value } = item as unknown as Item;
      return !isSelected(value);
    });

    const getFilteredItems = (): Item[] | GroupedItem[] => {
      if (isGroupedItems) {
        return (preFilteredItems as GroupedItem[]).map((item) => {
          const items = item.items.filter(({ value }) => !isSelected(value));

          return {
            ...item,
            items,
          };
        });
      }

      return preFilteredItems as GroupedItem[];
    };

    return getFilteredItems();
  };

  const handleDelete = (e: Event | React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, index: number) => {
    // Remove the selection if the close icon is pressed and repopulate the menu with unselected items
    e.stopPropagation();
    setCurrentSearchTerm("");

    const newSelection = _cloneDeep(selection);
    newSelection.splice(index, 1);
    setSelection(newSelection);

    setAvailableItems(unSelectedItems);
    menuRef?.current?.focus();
  };

  const triggerSelect = (item: Item) => {
    close();
    menuRef?.current?.focus();
    if (selectable) {
      const newSelection = selection ? _cloneDeep(selection) : [];
      setSelection(newSelection.concat(item));
    }
  };

  const searchFilter = (item: Item): boolean => {
    return Object.values(item).toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase());
  };

  React.useEffect(() => {
    if (onSearch) {
      onSearch(debouncedSearchTerm.toLowerCase());
    } else {
      let isGroupedItems = false;
      const preFilteredItems = (items as GroupedItem[]).filter((item: GroupedItem) => {
        if (item.items) {
          isGroupedItems = true;
          return item.items.some(searchFilter);
        }

        return searchFilter(item as unknown as Item);
      });

      const getFilteredItems = (): Item[] | GroupedItem[] => {
        if (isGroupedItems) {
          return (preFilteredItems as GroupedItem[]).map((item) => {
            const items = item.items.filter(searchFilter);

            return {
              ...item,
              items,
            };
          });
        }

        return preFilteredItems as GroupedItem[];
      };

      setSearching ? setSearching(false) : setShowingSpinner(false);
      setAvailableItems(getFilteredItems());
    }
  }, [debouncedSearchTerm]);

  React.useEffect(() => {
    // if there has been a selection we should clear the search term
    if (selection) {
      setCurrentSearchTerm("");
    }
    // filter out selected values
    setAvailableItems(unSelectedItems);
  }, [selection, items]);

  React.useEffect(() => {
    setShowingSpinner(isSearching);
  }, [isSearching]);

  const renderSelectMenuRow = (
    { label, value, subtext, icon }: Item,
    index: number,
    externalIndex?: number,
    name?: string
  ): React.ReactNode => {
    const shouldRenderGroupTitle = index === 0 && name;

    const getSelectedItem = (): Item => {
      if (externalIndex !== undefined && (availableItems[externalIndex] as GroupedItem)?.items) {
        return (availableItems[externalIndex] as GroupedItem).items[index];
      }

      return (availableItems as Item[])[index];
    };

    return (
      <React.Fragment key={index}>
        {shouldRenderGroupTitle && (
          <React.Fragment>
            <GroupTitle>{name}</GroupTitle>
            {required && <RequiredAsterisk>*</RequiredAsterisk>}
          </React.Fragment>
        )}
        <Option
          role="menuitem"
          key={value}
          onClick={() => {
            triggerSelect(getSelectedItem());
          }}
          onKeyDown={onComboBoxKeyDown}
          onKeyUp={(e) => {
            onItemKeyUp(e);

            // Focus the input if at the first menu item and pressing the up arrow key
            if (index === 0 && !topOfMenuReached) {
              setTopOfMenuReached(true);
            } else if (index === 0 && topOfMenuReached && KEY_ARROW_UP === e.key) {
              setTopOfMenuReached(false);
              inputRef?.current?.focus();
            } else {
              setTopOfMenuReached(false);
            }
          }}
          {...rover}
        >
          <OptionText>
            {icon && <CustomIcon icon={icon} />}
            {label}
            {subtext && <OptionSubtext>{subtext}</OptionSubtext>}
          </OptionText>
        </Option>
      </React.Fragment>
    );
  };

  return (
    <SelectMenuContainer disabled={disabled} className={className}>
      {description && (
        <Description>
          {description}
          {required && <RequiredAsterisk>*</RequiredAsterisk>}
        </Description>
      )}
      <DropdownContainer>
        <Trigger
          id={id}
          role="combobox"
          aria-haspopup="true"
          aria-autocomplete="list"
          aria-owns="#combobox-dropdown"
          aria-expanded={isMenuOpen}
          className={sensitive ? "fs-exclude" : ""}
          tabIndex={0}
          onClick={(e) => {
            if (document.activeElement !== inputRef?.current) {
              onClick(e);
            }
          }}
          onKeyUp={(e) => {
            if (!selection) {
              onKeyUp(e);
            }
          }}
          onKeyDown={(e) => {
            onComboBoxKeyDown(e);
            // Delete selection if in focus and backspace, delete, or esc key is pressed
            if (selection && [KEY_BACKSPACE, KEY_DELETE, KEY_ESCAPE].includes(e.key)) {
              handleDelete(e, selection.length - 1);
              menuRef?.current?.focus();
            }
          }}
          ref={menuRef}
          invalid={invalid}
          uneditable={uneditable}
        >
          {selection && (
            <SelectedItems>
              {selection.map((item, index) => {
                return (
                  <SelectedItem key={index}>
                    <OptionText>
                      {item.icon && <CustomIcon icon={item.icon} />} {item.label}{" "}
                      {item.subtext && <OptionSubtext>{item.subtext}</OptionSubtext>}
                    </OptionText>
                    <CloseIconWrapper onClick={(e) => handleDelete(e, index)}>
                      <Icon icon={"close"} />
                    </CloseIconWrapper>
                  </SelectedItem>
                );
              })}
            </SelectedItems>
          )}
          {(!selection || selection.length === 0) && (
            <ComboBoxInput
              id={searchInputId}
              ref={inputRef}
              type="search"
              placeholder={placeholder}
              value={currentSearchTerm}
              onFocus={() => {
                open();
              }}
              onKeyDown={(e) => {
                onComboBoxKeyDown(e);
                open();

                // Focus on first menu item when pressing down arrow from input if there are search results
                if (availableItems.length > 0 && KEY_ARROW_DOWN === e.key) {
                  rover.stops[0].ref.current?.focus();
                }

                if (KEY_ESCAPE === e.key) {
                  setCurrentSearchTerm("");
                }
              }}
              onKeyUp={onKeyUp}
              onChange={(e) => {
                // Start loading and update search term value
                setSearching ? setSearching(true) : setShowingSpinner(true);
                setCurrentSearchTerm(e.currentTarget.value);
              }}
            />
          )}
          {invalid && <ErrorIcon large icon="error" />}
          <ArrowIconWrapper>
            <Icon icon={isMenuOpen ? "arrow-up" : "arrow-down"} />
          </ArrowIconWrapper>
        </Trigger>
        <DropdownHandler
          mobileTitle={placeholder || "Select an option"}
          mobileAlignment="fixed-bottom"
          clickAway={(e) => {
            if (document.activeElement !== inputRef?.current) {
              close();
            }
            e.stopPropagation();
          }}
          inProp={isMenuOpen}
          renderInPortal={renderInPortal}
        >
          <Dropdown coords={coords} updateCoords={updateCoords} shouldUseCoords={shouldUseCoords}>
            <DropdownElement
              id="combobox-dropdown"
              className={`dropdown-wrap${sensitive ? " fs-exclude" : ""}`}
              role="menu"
              aria-orientation="vertical"
              aria-label={description}
            >
              {isShowingSpinner && (
                <Option role="menuitem" {...rover}>
                  <OptionText>
                    <LoaderWrapper>
                      <LoadingState /> <LoaderText>Searching</LoaderText>
                    </LoaderWrapper>
                  </OptionText>
                </Option>
              )}

              {!isShowingSpinner && availableItems.length === 0 && (
                <Option role="menuitem" {...rover}>
                  {!items ||
                  items.length === 0 ||
                  // When all selectable items are already selected
                  selection.length === items.length ? (
                    <OptionText>No options available</OptionText>
                  ) : (
                    <OptionText>No results containing &ldquo;{currentSearchTerm}&rdquo;</OptionText>
                  )}
                </Option>
              )}

              {!isShowingSpinner &&
                (availableItems as GroupedItem[]).map((item: GroupedItem, index) => {
                  if (item.items) {
                    return (
                      <div role="group" key={index}>
                        {(item.items as Item[]).map((innerItem, innerIndex) =>
                          renderSelectMenuRow(innerItem, Number(innerIndex), Number(index), item.name)
                        )}
                      </div>
                    );
                  }

                  return renderSelectMenuRow(item as unknown as Item, Number(index));
                })}
            </DropdownElement>
          </Dropdown>
        </DropdownHandler>
      </DropdownContainer>
      {invalid && <ErrorMessage>{errorMessage ? errorMessage : "An error has occurred"}</ErrorMessage>}
    </SelectMenuContainer>
  );
};
